import { useAtomValue } from 'jotai';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from './ordered-layer';
import { createMapSourceAtom } from './atoms';
import { MapUserConfigAtom } from './types';
import React, { useMemo } from 'react';
import { focusAreaCategoryColors, defaultFocusAreaColor } from './map-colors';

/** Combined Fault layer id from backend; we do not show a label on the map for this layer. */
const FAULT_COMBINED_LAYER_ID = 'category:Fault';

/**
 * Data-driven fill/line color by tile feature category (matches selector checkbox colors).
 * @return {(string|unknown[])[]} Mapbox expression array for match(category) → color.
 */
function focusAreaColorExpression(): (string | unknown[])[] {
  const match: (string | unknown[])[] = ['match', ['get', 'category']];
  Object.entries(focusAreaCategoryColors).forEach(([category, color]) => {
    match.push(category, color);
  });
  match.push(defaultFocusAreaColor);
  return match;
}

/**
 * An area's own `display_color` wins, so a colour chosen in the Area Builder shows
 * on every map rather than only on the area's detail page. The tile leaves it NULL
 * for areas that were never given one — and on the merged Fault layer — so those
 * fall back to the category colour.
 */
const focusAreaColorExpr = [
  'coalesce',
  ['get', 'display_color'],
  focusAreaColorExpression(),
];

const FocusAreaLayer = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const areaGeometriesSourceAtom = useMemo(
    () => createMapSourceAtom('areaGeometries'),
    []
  );
  const areaGeometriesSource = useAtomValue(areaGeometriesSourceAtom);

  const mapConfig = useAtomValue(mapConfigAtom);
  if (!areaGeometriesSource) return null;

  // Get all selected areas across all categories
  const selectedAreas = Object.values(
    mapConfig.focusAreas?.selections || {}
  ).flatMap((areas) => areas);

  if (selectedAreas.length === 0) return null;

  return (
    <LayerBand band={LAYER_ORDER.focusAreas}>
      <Source id="focus-areas" type="vector" url={areaGeometriesSource}>
        {selectedAreas.map((area) => (
          <React.Fragment key={area}>
            <Layer
              source-layer="focus-areas"
              id={`focus-area-fill-${area}`}
              source="focus-areas"
              type="fill"
              paint={{
                // Mapbox expression: match category to focus area color (data-driven)
                'fill-color': focusAreaColorExpr as never,
                'fill-opacity': 0.25,
              }}
              filter={['==', ['get', 'uuid'], area]}
            />
            <Layer
              source="focus-areas"
              source-layer="focus-areas"
              id={`focus-area-line-${area}`}
              type="line"
              paint={{
                // Mapbox expression: match category to focus area color (data-driven)
                'line-color': focusAreaColorExpr as never,
                'line-width': 3,
                'line-opacity': 1,
              }}
              filter={['==', ['get', 'uuid'], area]}
            />
            {/* Only show labels for non-fault areas */}
            {area !== FAULT_COMBINED_LAYER_ID && (
              <Layer
                source="focus-areas"
                source-layer="focus-areas"
                id={`focus-area-label-${area}`}
                type="symbol"
                layout={{
                  'text-anchor': 'center',
                  'text-field': '{sub_category}',
                  'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
                  'symbol-placement': 'point',
                  'text-allow-overlap': false,
                  'text-ignore-placement': false,
                  'text-letter-spacing': 0.05,
                  'text-padding': 20,
                }}
                paint={{
                  'text-color': '#000',
                  'text-halo-color': '#fff',
                  'text-halo-width': 1,
                }}
                filter={['==', ['get', 'uuid'], area]}
              />
            )}
          </React.Fragment>
        ))}
      </Source>
    </LayerBand>
  );
};

export { FocusAreaLayer };
